import { useRef, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { SurveyInstance } from '@/types/survey.types';
import { MOCK_DASHBOARD } from '@/data/mockSurveys';
import { usePendingInstances, useDashboardStats, useCurrentScore, useSubmitResponse, isSurveyUnavailableError } from '@/hooks/usePendingInstances';
import { VIEW_PATHS } from '@/router/pulsoRoutes';
import { HomeHero } from './components/HomeHero';
import { ActiveSurveyInline } from './components/ActiveSurveyInline';
import { HomeKpiStrip } from './components/HomeKpiStrip';
import { GrowthBanner } from './components/GrowthBanner';
import { RecognitionBanner } from './components/RecognitionBanner';
import { HomeTips } from './components/HomeTips';
import { useSurveyQuiz } from './hooks/useSurveyQuiz';
import { useUserContext, canAccessRecognitionEntry } from '@/contexts/UserContext';
import './SurveyView.css';

// ─── Inner component (receives pre-loaded instances) ─────────────────────────

interface SurveyViewLoadedProps {
  instances: SurveyInstance[];
  stats: typeof MOCK_DASHBOARD.stats;
  currentScore: number;
}

type ToastState = { type: 'success' | 'error'; msg: string } | null;

function SurveyViewLoaded({ instances, stats, currentScore }: SurveyViewLoadedProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pending, quiz, omitSurvey, continueAfterComplete } = useSurveyQuiz(instances);
  const { mutate: submitResponse } = useSubmitResponse();
  const [toast, setToast] = useState<ToastState>(null);

  // RF-011 — El banner de Reconocimiento es el punto de entrada a Shine Together desde Pulso.
  // Se condiciona con el MISMO gate por rol que usa la propia entrada `reconocer` del otro
  // armazón (canAccessRecognitionEntry), no con `canViewBrillamos`: por RF-011 todo usuario con
  // acceso a Pulso llega a Reconocimiento por default, y todos los roles de Pulso están dentro de
  // RECOGNITION_GENERAL_ROLES, así que el banner se muestra a todo usuario de Pulso.
  const { demoUser } = useUserContext();
  const canReachRecognition = canAccessRecognitionEntry('reconocer', demoUser.role);

  const initialTotal = useRef(instances.length);
  const surveysAnswered = initialTotal.current - pending.length;
  const currentInstance = quiz?.instance ?? null;

  // Auto-dismiss toast after 3 s
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(id);
  }, [toast]);

  // Una encuesta que ya venció (o que otro dispositivo acaba de contestar) no se arregla
  // reintentando: el mensaje debe decir que dejó de estar disponible, no "intenta de nuevo".
  const errorMessage = useCallback((err: unknown, fallbackKey: string) => (
    isSurveyUnavailableError(err)
      ? t('survey.unavailableError', 'Esta encuesta ya no está disponible')
      : t(fallbackKey, 'Error — intenta de nuevo')
  ), [t]);

  const handleComplete = useCallback((answers: Record<string, string | number>) => {
    if (currentInstance) {
      submitResponse(
        { instanceId: currentInstance.id, answers, questions: currentInstance.questions },
        {
          onSuccess: () => setToast({ type: 'success', msg: t('survey.submitSuccess', 'Respuestas enviadas') }),
          onError:   (err: unknown) => setToast({ type: 'error', msg: errorMessage(err, 'survey.submitError') }),
        },
      );
    }
    continueAfterComplete();
  }, [currentInstance, submitResponse, continueAfterComplete, errorMessage, t]);

  const handleOmit = useCallback(() => {
    omitSurvey({
      onSuccess: () => setToast({ type: 'success', msg: t('survey.omitSuccess', 'Encuesta omitida') }),
      onError:   (err: unknown) => setToast({ type: 'error', msg: errorMessage(err, 'survey.omitError') }),
    });
  }, [omitSurvey, t, errorMessage]);

  return (
    <div className="survey-view">
      <HomeHero pendingCount={pending.length} />

      {currentInstance ? (
        <ActiveSurveyInline
          key={currentInstance.id}
          instance={currentInstance}
          totalAnswered={surveysAnswered}
          totalSurveys={initialTotal.current}
          stackCount={pending.length - 1}
          onOmit={handleOmit}
          onComplete={handleComplete}
          hasNext={pending.length > 1}
        />
      ) : (
        <div className="home-allclear">
          <img src="/assets/persona-asomada.svg" alt="" style={{ height: 64, opacity: 0.8 }} />
          <div>
            <div className="home-allclear-title">{t('survey.allDoneTitle')}</div>
            <div className="home-allclear-sub">{t('survey.allDoneSubtitle')}</div>
          </div>
        </div>
      )}

      <HomeKpiStrip stats={stats} />
      {canReachRecognition && <RecognitionBanner />}
      <GrowthBanner onGrowthClick={() => navigate(VIEW_PATHS.safe)} />
      <HomeTips currentScore={currentScore} />

      {toast && (
        <div className={`sv-toast ${toast.type}`} role="status" aria-live="polite">
          <i className={`fas fa-${toast.type === 'success' ? 'check-circle' : 'exclamation-circle'}`} />
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── Shell: fetches data, shows loading skeleton while pending ────────────────

export function SurveyView() {
  const { t } = useTranslation();
  const { data: instances, isLoading: loadingInst } = usePendingInstances();
  const { data: stats, isLoading: loadingStats } = useDashboardStats();
  const { data: currentScore, isLoading: loadingScore } = useCurrentScore();

  if (loadingInst || loadingStats || loadingScore) {
    return (
      <div className="survey-view">
        <div className="home-loading" aria-busy="true">
          <div className="home-loading-spinner">
            <img src="/favicon.png" alt="" />
          </div>
          <span className="home-loading-text">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <SurveyViewLoaded
      instances={instances ?? []}
      stats={stats ?? MOCK_DASHBOARD.stats}
      currentScore={currentScore ?? MOCK_DASHBOARD.currentScore}
    />
  );
}
